import { vn } from 'vend-number'
import { useT } from '@transifex/react'
import { useMemo } from 'react'
import { createSelector } from '@reduxjs/toolkit'
import {
  hasPayments,
  isConfirmedNotReadyToComplete,
} from '../../../../functions/sale'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectLineItems,
  selectOutstandingBalance,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { SaleStatus } from '../../../../models/sale.constants'
import { RootState } from '../../../../state/store'
import { LineItem } from '../../../../models/line-item'

const selectData = createSelector(
  [
    (state: RootState) => state.currentSale.status,
    (state: RootState) => state.currentSale.readyToComplete,
    (state: RootState) => state.currentSale.isSubmittedSaleEarly,
    (state: RootState) => state.currentSale.payments,
    selectOutstandingBalance,
    (state: RootState) => state.currentSale.displayStatus,
  ],
  (
    status,
    readyToComplete,
    isSubmittedSaleEarly,
    payments,
    outStandingBalance,
    displayStatus
  ) => ({
    status,
    readyToComplete,
    isSubmittedSaleEarly,
    payments,
    outStandingBalance,
    displayStatus,
  })
)

const hasNegativeQuantityLineItems = (lineItems: LineItem[]) =>
  lineItems.some(lineItem => Number(lineItem.quantity) < 0)

export const useSalePaymentLabel = () => {
  const data = useAppSelector(selectData)
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const lineItems = useAppSelector(selectLineItems)
  const t = useT()

  const COMPLETE_SALE_LABELS = useMemo(
    () => ({
      BALANCE: t('Balance', { _context: 'Label for pay button' }),
      EXCHANGE: t('Exchange', { _context: 'Label for pay button' }),
      PAY: t('Pay', { _context: 'Label for pay button' }),
      REFUND: t('Refund', { _context: 'Label for pay button' }),
    }),
    [t]
  )

  if (isConfirmedNotReadyToComplete(data) || hasPayments(data)) {
    if (vn(outstandingBalance).lessThan(0)) {
      return COMPLETE_SALE_LABELS.REFUND
    } else {
      return COMPLETE_SALE_LABELS.PAY
    }
  } else if (data.displayStatus === SaleStatus.RETURN_PARKED) {
    if (vn(outstandingBalance).lessThan(0)) {
      return COMPLETE_SALE_LABELS.REFUND
    } else {
      return COMPLETE_SALE_LABELS.PAY
    }
  } else if (data.displayStatus === SaleStatus.EXCHANGE_PARKED) {
    return COMPLETE_SALE_LABELS.EXCHANGE
  }

  if (hasNegativeQuantityLineItems(lineItems)) {
    if (vn(outstandingBalance).equals(0)) {
      return COMPLETE_SALE_LABELS.EXCHANGE
    }
    if (vn(outstandingBalance).lessThan(0)) {
      return COMPLETE_SALE_LABELS.REFUND
    }
  }

  return COMPLETE_SALE_LABELS.PAY
}
